/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import {
  SafText,
  SafTab,
  SafTabs,
  SafTabPanel,
  SafTable,
  SafButton,
  SafCard,
  SafIcon,
  SafCombobox,
  SafOption,
  SafPagination,
  SafDialog,
  SafAlert,
  SafAnchor,
  SafCheckbox,
} from "@saffron/core-components/react";
import "../../../App.css";
import "../../../css/breadcrumb.css";
import "../../../css/common.css";
import { callMiddleware } from "../../../util/api";
import { ENVIRONMENTS } from "../../../util/constants";
import { CustomHeader } from "../../../shared";

const JustificationComponent = ({ techStack }) => {
  // Extract assetId from URL params
  const { assetId: urlAssetId } = useParams();
  const navigate = useNavigate();

  // State to store asset information
  const [assetInfo, setAssetInfo] = useState(null);

  const [runtimeData, setRuntimeData] = useState([]);
  const [techDetailsMap, setTechDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlled, setControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [5, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 25,
  });
  const [filterValue, setFilterValue] = useState("Proposed");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deviations, setDeviations] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [currentServiceId, setCurrentServiceId] = useState(null);

  const Data = [
    { title: "Proposed Score", percentage: <em>coming soon...</em> },
    { title: "Non-Production Score", percentage: <em>coming soon...</em> },
    { title: "Production Score", percentage: <em>coming soon...</em> },
    { title: "Issues", percentage: <em>coming soon...</em> },
  ];

  const [showAll, setShowAll] = useState(false);
  const visiblecards = showAll ? Data : Data.slice(0, 4);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  // Set initial selectedTab from localStorage or default to 'Frontend'
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem("selectedTab") || "Frontend";
  });
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [alertMessage, setAlertMessage] = useState("");
  // Add this state near your other useState declarations
  const [isJustificationEditable, setIsJustificationEditable] = useState(false);

  const [showDescription, setShowDescription] = useState(false);

  // state variables for editing justifications
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingJustifications, setEditingJustifications] = useState({});

  // Add this function to handle the Add/Edit button click
  const handleAddEditJustification = () => {
    setIsJustificationEditable(true);
  };

  // Handler for pattern checkbox changes
  const handlePatternCheckboxChange = (serviceId, patternName, isChecked) => {
    const key = `${serviceId}-${patternName}`;
    setSelectedPatterns((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  };

  // Handler for remove patterns button click
  const handleGlobalRemovePatternsClick = () => {
    if (selectedPatterns.size === 0) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Please select at least one pattern to remove.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
      return;
    }

    setRemoveDialogOpen(true);
  };

  // Confirm pattern removal
  const confirmPatternRemoval = async () => {
    setRemoveDialogOpen(false);

    try {
      if (selectedPatterns.size === 0) return;

      // Group selected pattern_ref_ids by service ID
      const patternsByService = {};
      Array.from(selectedPatterns).forEach((key) => {
        const parts = key.split("-");
        const serviceId = parts[0];
        const patternRefId = parts.slice(1).join("-"); // Handle IDs with dashes

        if (!patternsByService[serviceId]) {
          patternsByService[serviceId] = [];
        }
        patternsByService[serviceId].push(patternRefId);
      });

      // Fetch current data
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Update the data by removing selected pattern_ref_ids from all services
      const updatedTechStacks = currentTechStacks.map((item) => {
        if (
          (selectedTab === "Frontend" && item.tech_stack === "frontend") ||
          (selectedTab === "Backend" && item.tech_stack === "backend") ||
          (selectedTab === "DevOps" && item.tech_stack === "devops")
        ) {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            if (env.services && Array.isArray(env.services)) {
              return {
                ...env,
                services: env.services.map((service) => {
                  // Check if this service has patterns to remove
                  if (patternsByService[service.service_id]) {
                    // CHANGED: Filter pattern_ref_id instead of pattern_names
                    const updatedPatternRefIds = (
                      service.pattern_ref_id || []
                    ).filter((refId) => {
                      const shouldKeep =
                        !patternsByService[service.service_id].includes(refId);
                      return shouldKeep;
                    });

                    return {
                      ...service,
                      pattern_ref_id: updatedPatternRefIds, // CHANGED: Update pattern_ref_id
                    };
                  }
                  return service;
                }),
              };
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        return item;
      });

      // Update the database
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: currentDeviations,
      };

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      // Update local state
      setRuntimeData(updatedTechStacks);

      // Clear all selected patterns
      setSelectedPatterns(new Set());

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Selected patterns removed successfully!");

      // Auto-hide success message
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    } catch (err) {
      console.error("Error removing patterns:", err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to remove patterns. Please try again.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    }
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
  };

  // Handle URL parameter and localStorage synchronization
  useEffect(() => {
    const fetchAssetNameById = async (id) => {
      if (!id) return;

      try {
        const dbName = process.env.REACT_APP_DB_SOURCE;
        const data = await callMiddleware("get", {
          dbName,
          containerName:
            process.env.REACT_APP_CONTAINER_ASSET_INSIGHT_DATASOURCE,
          query: `SELECT c.id, c.asset_name FROM c WHERE c.id = '${id}'`,
        });
        if (data && data.length > 0) {
          const essentialInfo = {
            id: data[0].id,
            asset_name: data[0].asset_name,
          };
          // Update both state and localStorage
          localStorage.setItem("assetInfo", JSON.stringify(essentialInfo));
          setAssetInfo(essentialInfo);
        }
      } catch (error) {
        console.error("Error fetching asset name:", error);
      }
    };

    if (urlAssetId) {
      // Always fetch fresh data when URL changes
      fetchAssetNameById(urlAssetId);
    } else if (localStorage.getItem("assetInfo")) {
      // If no URL param but we have localStorage data, redirect
      const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
      if (storedInfo.id) {
        navigate(`/${storedInfo.id}/technology-stacks`, { replace: true });
      } else {
        // No valid data, go to home
        navigate("/home");
      }
    } else {
      // No data at all, go to home
      navigate("/home");
    }
  }, [urlAssetId, navigate]);

  // Handle individual row edit
  const handleRowEdit = (techId) => {
    setEditingRowId(techId);
    setEditingJustifications({
      [techId]: justifications[techId] || "",
    });
  };

  // Handle individual row save
  const handleRowSave = async (techId) => {
    if (
      !editingJustifications[techId] ||
      editingJustifications[techId].trim() === ""
    ) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Justification cannot be empty.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
      return;
    }

    try {
      // Update justifications state
      setJustifications((prev) => ({
        ...prev,
        [techId]: editingJustifications[techId],
      }));

      // Save to database (reuse your existing saveJustifications logic but for single row)
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Update deviations: support both service_id and service_name
      let updatedDeviations = [...currentDeviations];
      const existingIndex = updatedDeviations.findIndex(
        (dev) => dev.service_id === techId || dev.service_name === techId
      );

      if (existingIndex >= 0) {
        updatedDeviations[existingIndex].justification =
          editingJustifications[techId].trim();
      } else {
        // Determine if techId is an ID or a name
        const isId = /^[a-zA-Z0-9\-_]+$/.test(techId) && !techId.includes(" ");
        if (isId) {
          updatedDeviations.push({
            service_id: techId,
            justification: editingJustifications[techId].trim(),
          });
        } else {
          updatedDeviations.push({
            service_name: techId,
            justification: editingJustifications[techId].trim(),
          });
        }
      }

      // Update database
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: currentTechStacks,
        deviations: updatedDeviations,
      };

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      setDeviations(updatedDeviations);
      setEditingRowId(null);
      setEditingJustifications({});

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Justification saved successfully!");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    } catch (err) {
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to save justification. Please try again.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    }
  };

  // Handle individual row cancel
  const handleRowCancel = () => {
    setEditingRowId(null);
    setEditingJustifications({});
  };

  // Handle individual justification change
  const handleEditingJustificationChange = (techId, value) => {
    setEditingJustifications((prev) => ({
      ...prev,
      [techId]: value,
    }));
  };

  const handleTabChange = (event) => {
    // Support both custom and native events
    let tabId = null;
    if (event && event.detail && event.detail.id) {
      tabId = event.detail.id;
    } else if (event && event.target && event.target.id) {
      tabId = event.target.id;
    } else if (typeof event === "string") {
      tabId = event;
    }
    if (tabId && tabId !== selectedTab) {
      setSelectedTab(tabId);
      setControlled((prev) => ({
        ...prev,
        currentPageIndex: 1,
      }));
      fetchTechnologyStacksData();
      fetchTechStackDetails();
    }
  };

  // Fetch tech stack mapping data from source database
  const fetchTechStackDetails = async () => {
    try {
      const dbName = process.env.REACT_APP_DB_SOURCE;
      const containerId = "tech-stack-mapping";
      const query = "SELECT * FROM c";

      const resources = await callMiddleware("get", {
        dbName,
        containerName: containerId,
        query,
      });

      // Create ONE global pattern lookup for all services
      const globalPatternLookup = {};
      const techDetailsMap = {};

      if (Array.isArray(resources)) {
        resources.forEach((item) => {
          if (item.capabilities && Array.isArray(item.capabilities)) {
            item.capabilities.forEach((capability) => {
              if (capability.services && Array.isArray(capability.services)) {
                capability.services.forEach((service) => {
                  // Build the global pattern lookup using pattern_ref_id as key
                  if (
                    service.supportingPattern &&
                    Array.isArray(service.supportingPattern)
                  ) {
                    service.supportingPattern.forEach((pattern) => {
                      // CHANGED: Use pattern_ref_id instead of pattern_name as the key
                      globalPatternLookup[pattern.pattern_ref_id] = pattern;
                    });
                  }

                  // Store service details without individual pattern lookup
                  techDetailsMap[service.tech_radar_id] = {
                    categoryName: item.category || "",
                    technologyName: service.technology_name || "",
                    capability: capability.capability || "",
                    recommendation: service.recommendation || "",
                    provider: service.provider || "",
                    description: service.description || "",
                  };
                });
              }
            });
          }
        });
      }

      // Store both the tech details and the global pattern lookup
      setTechDetailsMap({
        ...techDetailsMap,
        _globalPatternLookup: globalPatternLookup,
      });
    } catch (error) {
      console.error("Error fetching tech stack details from middleware", error);
    }
  };

  // Fetch technology stacks data for the current asset
  const fetchTechnologyStacksData = useCallback(async () => {
    if (!urlAssetId) {
      console.error("No asset ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const data = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      // Check if data exists and has the expected structure
      if (
        data &&
        data.technology_stacks &&
        Array.isArray(data.technology_stacks)
      ) {
        setRuntimeData(data.technology_stacks);
        setDeviations(data.deviations || []);
      } else {
        // If the middleware returns an array directly (first item is what we want)
        if (
          Array.isArray(data) &&
          data.length > 0 &&
          data[0].technology_stacks
        ) {
          setRuntimeData(data[0].technology_stacks);
          setDeviations(data[0].deviations || []);
        } else {
          console.warn("Unexpected data structure:", data);
          setRuntimeData([]);
          setDeviations([]);
        }
      }
    } catch (err) {
      console.error("Error fetching technology stacks data:", err);
      setError(
        "Failed to fetch technology stack data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [urlAssetId]);

  useEffect(() => {
    // First fetch the technology stacks data
    fetchTechnologyStacksData();
    // Then fetch the tech stack details to map service IDs to their details
    fetchTechStackDetails();
  }, [fetchTechnologyStacksData]);

  // Remove any reload or navigation detection logic

  const filterByEnvironment = useMemo(() => {
    return runtimeData
      .map((item) => {
        if (!filterValue || filterValue === "All") {
          return item;
        }
        const filteredEnvironments = item.tech_stack_environment.filter(
          (env) => env.environment === filterValue
        );
        return {
          ...item,
          tech_stack_environment: filteredEnvironments,
        };
      })
      .filter(
        (item) =>
          !filterValue ||
          filterValue === "All" ||
          item.tech_stack_environment.length > 0
      );
  }, [runtimeData, filterValue]);

  const filteredData = useMemo(() => {
    return runtimeData.filter((item) => {
      // Make sure tech_stack_environment exists and is an array
      if (
        !item.tech_stack_environment ||
        !Array.isArray(item.tech_stack_environment)
      ) {
        return false;
      }

      // Filter by selected tab
      const matchesTab =
        (selectedTab === "Frontend" &&
          (item.tech_stack === "frontend" || !item.tech_stack)) ||
        (selectedTab === "Backend" && item.tech_stack === "backend") ||
        (selectedTab === "DevOps" && item.tech_stack === "devops");

      if (!matchesTab) return false;

      // If no environment filter is applied, include all items that match the tab
      if (!filterValue || filterValue === "All") return true;

      // Only include items that have the selected environment
      return item.tech_stack_environment.some(
        (env) => env.environment === filterValue
      );
    });
  }, [runtimeData, filterValue, selectedTab]);

  const paginatedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const allServices = [];

    // Extract the global pattern lookup once
    const globalPatternLookup = techDetailsMap._globalPatternLookup || {};

    filteredData.forEach((item) => {
      if (
        item.tech_stack_environment &&
        Array.isArray(item.tech_stack_environment)
      ) {
        const environments =
          !filterValue || filterValue === "All"
            ? item.tech_stack_environment
            : item.tech_stack_environment.filter(
                (env) => env.environment === filterValue
              );

        environments.forEach((env) => {
          // Handle new structure with services array
          if (env.services && Array.isArray(env.services)) {
            env.services.forEach((service) => {
              const techDetails = techDetailsMap[service.service_id];
              if (techDetails) {
                // CHANGED: Use pattern_ref_id instead of pattern_names
                const fullPatterns = (service.pattern_ref_id || [])
                  .map((refId) => {
                    return globalPatternLookup[refId] || null;
                  })
                  .filter(Boolean);

                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: service.service_id,
                  // Use the reconstructed full pattern objects
                  supportingPattern: fullPatterns,
                });
              } else {
                allServices.push({
                  categoryName: "Unknown",
                  technologyName:
                    service.service_name || `Service ID: ${service.service_id}`,
                  capability: "Unknown",
                  recommendation: "Unknown",
                  provider: "Unknown",
                  environment: env.environment,
                  tech_radar_id: service.service_id || service.service_name,
                  supportingPattern: [],
                });
              }
            });
          }
          // Handle old structure with service_ids array (backward compatibility)
          else if (env.service_ids && Array.isArray(env.service_ids)) {
            env.service_ids.forEach((serviceId) => {
              const techDetails = techDetailsMap[serviceId];
              if (techDetails) {
                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: techDetails.supportingPattern || [],
                });
              } else {
                allServices.push({
                  categoryName: "Unknown",
                  technologyName: `Service ID: ${serviceId}`,
                  capability: "Unknown",
                  recommendation: "Unknown",
                  provider: "Unknown",
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: [],
                });
              }
            });
          }
        });
      }
    });

    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;

    // Apply sorting and pagination
    let sortedServices = [...allServices];

    if (sortField) {
      sortedServices.sort((a, b) => {
        let valueA, valueB;

        switch (sortField) {
          case "Category":
            valueA = a.categoryName || "";
            valueB = b.categoryName || "";
            break;
          case "Capability":
            valueA = a.capability || "";
            valueB = b.capability || "";
            break;
          case "TechnologyName":
            valueA = a.technologyName || "";
            valueB = b.technologyName || "";
            break;
          case "Recommendation":
            valueA = a.recommendation || "";
            valueB = b.recommendation || "";
            break;
          case "Provider":
            valueA = a.provider || "";
            valueB = b.provider || "";
            break;
          default:
            return 0;
        }

        if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
        if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    const paginatedServices = sortedServices.slice(startIndex, endIndex);
    return paginatedServices;
  }, [
    filteredData,
    controlled.currentPageIndex,
    controlled.itemsPerPage,
    techDetailsMap,
    sortField,
    sortOrder,
    filterValue,
  ]);
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(totalItems / controlled.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({ ...prev, currentPageIndex: event.detail }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  const totalItems = useMemo(() => {
    const count = filteredData.reduce((total, item) => {
      return (
        total +
        item.tech_stack_environment.reduce((envCount, env) => {
          // Handle both new and old data structures
          if (env.services && Array.isArray(env.services)) {
            return envCount + env.services.length;
          } else if (env.service_ids && Array.isArray(env.service_ids)) {
            return envCount + env.service_ids.length;
          }
          return envCount;
        }, 0)
      );
    }, 0);
    return count;
  }, [filteredData]);

  useEffect(() => {
    setControlled((prev) => ({
      ...prev,
      totalItemCount: totalItems,
    }));
  }, [totalItems]);

  const handleDeleteClick = (serviceId, environment) => {
    setServiceToDelete({ id: serviceId, environment });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    if (!serviceToDelete) return;

    try {
      // First, get the latest data to ensure we're working with current state
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      // Handle different response formats
      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Update the data by removing the service
      const updatedTechStacks = currentTechStacks.map((item) => {
        // Only update the tech stack that matches the currently selected tab
        if (
          (selectedTab === "Frontend" && item.tech_stack === "frontend") ||
          (selectedTab === "Backend" && item.tech_stack === "backend") ||
          (selectedTab === "DevOps" && item.tech_stack === "devops")
        ) {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            // Only update the environment where the service needs to be removed
            if (env.environment === serviceToDelete.environment) {
              // Handle new structure
              if (env.services) {
                return {
                  ...env,
                  services: env.services.filter((service) => {
                    // Check both service_id and service_name for identification
                    const serviceIdentifier =
                      service.service_id || service.service_name;
                    return serviceIdentifier !== serviceToDelete.id;
                  }),
                };
              }
              // Handle old structure (backward compatibility)
              else if (env.service_ids) {
                return {
                  ...env,
                  service_ids: env.service_ids.filter(
                    (id) => id !== serviceToDelete.id
                  ),
                };
              }
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        // Return unchanged for other tech stacks
        return item;
      });

      // Update deviations - only keep deviations for service IDs/names that still exist in "Proposed" environment
      const proposedServiceIds = new Set();

      updatedTechStacks.forEach((stack) => {
        stack.tech_stack_environment.forEach((env) => {
          if (env.environment === "Proposed") {
            // Handle new structure
            if (env.services && Array.isArray(env.services)) {
              env.services.forEach((service) => {
                // Use service_id or service_name as identifier
                const serviceIdentifier =
                  service.service_id || service.service_name;
                const techDetails = techDetailsMap[service.service_id];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceIdentifier);
                  }
                } else if (service.service_name) {
                  // For services without techDetails (service_name only), add them to track deviations
                  proposedServiceIds.add(service.service_name);
                }
              });
            }
            // Handle old structure
            else if (env.service_ids && Array.isArray(env.service_ids)) {
              env.service_ids.forEach((serviceId) => {
                const techDetails = techDetailsMap[serviceId];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceId);
                  }
                }
              });
            }
          }
        });
      });

      // Filter deviations to only include those that should be in the deviations array
      const updatedDeviations = currentDeviations.filter((deviation) => {
        const deviationIdentifier =
          deviation.service_id || deviation.service_name;
        return proposedServiceIds.has(deviationIdentifier);
      });

      // Prepare the updated document
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: updatedDeviations,
      };

      // Update the database
      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      // Update local state with the latest data
      setRuntimeData(updatedTechStacks);
      setDeviations(updatedDeviations);

      // Reset pagination to first page
      setControlled((prev) => ({
        ...prev,
        currentPageIndex: 1,
      }));
    } catch (err) {
      console.error("Error deleting service:", err);
      // You might want to show an error message to the user here
    } finally {
      setServiceToDelete(null);
    }
  };

  // Helper to get frontend technologies with non-adopt recommendation
  const nonAdoptAllTechs = useMemo(() => {
    const allServices = [];

    // Extract the global pattern lookup once
    const globalPatternLookup = techDetailsMap._globalPatternLookup || {};

    runtimeData.forEach((stack) => {
      if (
        stack.tech_stack === techStack &&
        stack.tech_stack_environment &&
        Array.isArray(stack.tech_stack_environment)
      ) {
        stack.tech_stack_environment.forEach((env) => {
          if (env.environment !== "Proposed") return;

          // Handle NEW structure with services array
          if (env.services && Array.isArray(env.services)) {
            env.services.forEach((service) => {
              const techDetails = techDetailsMap[service.service_id];
              if (
                techDetails &&
                (techDetails.recommendation || "").toLowerCase() !== "adopt" &&
                (techDetails.recommendation || "").toLowerCase() !== "mandate"
              ) {
                allServices.push({
                  technologyName:
                    techDetails.technologyName ||
                    `Service ID: ${service.service_id}`,
                  tech_radar_id: service.service_id,
                  tech_stacks: [stack.tech_stack],
                  environments: [env.environment],
                });
              } else if (service.service_name) {
                allServices.push({
                  technologyName: service.service_name,
                  tech_radar_id: service.service_name, // Use service_name as identifier
                  tech_stacks: [stack.tech_stack],
                  environments: [env.environment],
                });
              }
            });
          }
          // Handle OLD structure with service_ids array (backward compatibility)
          else if (env.service_ids && Array.isArray(env.service_ids)) {
            env.service_ids.forEach((serviceId) => {
              const service = techDetailsMap[serviceId];
              if (
                service &&
                (service.recommendation || "").toLowerCase() !== "adopt" &&
                (service.recommendation || "").toLowerCase() !== "mandate"
              ) {
                allServices.push({
                  technologyName:
                    service.technologyName || `Service ID: ${serviceId}`,
                  tech_radar_id: serviceId,
                  tech_stacks: [stack.tech_stack],
                  environments: [env.environment],
                });
              } else if (serviceId.service_name) {
                allServices.push({
                  technologyName: serviceId.service_name,
                  tech_stacks: [stack.tech_stack],
                  environments: [env.environment],
                });
              }
            });
          }
        });
      }
    });

    // Deduplicate services efficiently
    const unique = {};
    allServices.forEach((s) => {
      if (!unique[s.tech_radar_id]) {
        unique[s.tech_radar_id] = {
          technologyName: s.technologyName,
          tech_radar_id: s.tech_radar_id,
          tech_stacks: new Set(s.tech_stacks),
          environments: new Set(s.environments),
        };
      } else {
        unique[s.tech_radar_id].tech_stacks.add(s.tech_stacks[0]);
        unique[s.tech_radar_id].environments.add(s.environments[0]);
      }
    });

    return Object.values(unique).map((item) => ({
      ...item,
      tech_stacks: Array.from(item.tech_stacks).join(", "),
      environments: Array.from(item.environments).join(", "),
    }));
  }, [runtimeData, techDetailsMap, techStack]); // techDetailsMap now includes global lookup

  const [justifications, setJustifications] = useState({});
  const [savingJustifications, setSavingJustifications] = useState(false);

  const handleJustificationChange = (techId, value) => {
    setJustifications((prev) => ({ ...prev, [techId]: value }));
  };

  const justificationRefs = useRef({});

  useEffect(() => {
    nonAdoptAllTechs.forEach((tech) => {
      const ref = justificationRefs.current[tech.tech_radar_id];
      if (ref) {
        ref.style.height = "auto";
        ref.style.height = ref.scrollHeight + "px";
      }
    });
  }, [nonAdoptAllTechs, justifications]);

  // Load existing justifications when data is fetched
  useEffect(() => {
    // Load justifications from top-level deviations
    const existingJustifications = {};
    deviations.forEach((deviation) => {
      // Handle both service_id and service_name
      const key = deviation.service_id || deviation.service_name;
      if (key) {
        existingJustifications[key] = deviation.justification;
      }
    });
    setJustifications(existingJustifications);
  }, [deviations]);

  // Save justifications function
  const saveJustifications = async () => {
    if (Object.keys(justifications).length === 0) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("No justifications to save.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
      return;
    }

    setSavingJustifications(true);

    try {
      // Fetch latest document
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      // Handle different response formats
      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Determine which service IDs should have deviations (only those in "Proposed" with non-adopt/mandate recommendations)
      const proposedServiceIds = new Set();

      currentTechStacks.forEach((stack) => {
        stack.tech_stack_environment.forEach((env) => {
          if (env.environment === "Proposed" && env.service_ids) {
            env.service_ids.forEach((serviceId) => {
              const service = techDetailsMap[serviceId];
              if (service) {
                const recommendation = (
                  service.recommendation || ""
                ).toLowerCase();
                if (
                  recommendation !== "adopt" &&
                  recommendation !== "mandate"
                ) {
                  proposedServiceIds.add(serviceId);
                }
              }
            });
          }
        });
      });

      // Start with existing deviations but filter to only those that should still be there
      let updatedDeviations = currentDeviations.filter((deviation) =>
        proposedServiceIds.has(deviation.service_id)
      );

      // Update justifications for existing deviations
      updatedDeviations.forEach((dev) => {
        if (
          Object.prototype.hasOwnProperty.call(justifications, dev.service_id)
        ) {
          dev.justification = justifications[dev.service_id].trim();
        }
      });

      // Add new deviations for justifications that are not already present
      Object.entries(justifications).forEach(([service_id, justification]) => {
        if (
          proposedServiceIds.has(service_id) &&
          !updatedDeviations.some((dev) => dev.service_id === service_id)
        ) {
          updatedDeviations.push({
            service_id,
            justification: justification.trim(),
          });
        }
      });

      // Prepare the updated document with top-level deviations
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: currentTechStacks,
        deviations: updatedDeviations,
      };

      // Update the database
      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      // Update local state with the latest data
      setRuntimeData(currentTechStacks);
      setDeviations(updatedDeviations);

      setIsJustificationEditable(false);

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Justifications saved successfully!");
    } catch (err) {
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to save justifications. Please try again.");
    } finally {
      setSavingJustifications(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "100%",
        margin: "0",
        marginTop: "10px",
      }}
    >
      {/* --- Non-Adopt Frontend Technologies Table (inside same scrollable area as main table) --- */}
      {nonAdoptAllTechs.length > 0 && (
        <div style={{ marginTop: "32px", maxWidth: "100%" }}>
          <div
            style={{
              marginBottom: "10px",
              fontSize: 15,
              color: "#222",
              background: "#fff",
              lineHeight: 1.5,
            }}
          >
            <div>
              A review of the technology stack has revealed technology stack
              components that do not have a Recommendation of "Adopt". Requires
              justification for their use from Tech Standards and Governance. To
              do this go to the TechStack selection and enter the justification
              reason and the S&G approver for this exception. The following
              table lists these technologies:
            </div>
            <div style={{ marginTop: 6 }}>
              For each technology component listed below provide explanation as
              to its use within the tech stack. This should include the reason
              for the deviation from Tech Radar Standards.
            </div>
          </div>

          <SafTable
            alternating-rows="true"
            density="inherit"
            header-background="default"
            inline-borders="true"
            style={{
              marginTop: 0,
              maxwidth: "100%",
              minWidth: "1400px",
              background: "#fff",
              marginLeft: 0,
              marginRight: 0,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    width: "500px",
                    height: 32,
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#222",
                    padding: "4px 8px",
                  }}
                >
                  Technology Name
                </th>
                <th
                  style={{
                    width: "1300px",
                    height: 32,
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#222",
                    padding: "4px 48px 4px 48px",
                  }}
                >
                  Justification
                </th>
                <th
                  style={{
                    width: "100px",
                    height: 32,
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#222",
                    padding: "4px 8px",
                  }}
                >
                  Edit
                </th>
              </tr>
            </thead>
            <tbody>
              {nonAdoptAllTechs.map((tech) => {
                const isEditing = editingRowId === tech.tech_radar_id;
                const isOtherRowEditing =
                  editingRowId && editingRowId !== tech.tech_radar_id;

                return (
                  <tr
                    key={tech.tech_radar_id}
                    style={{
                      backgroundColor: isEditing ? "#fff3cd" : "transparent",
                      opacity: isOtherRowEditing ? 0.5 : 1,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <td
                      style={{
                        color: "#222",
                        fontSize: 15,
                        fontWeight: 400,
                        height: 32,
                        verticalAlign: "middle",
                        padding: "4px 8px",
                      }}
                    >
                      {tech.technologyName}
                    </td>
                    <td
                      style={{
                        height: 32,
                        verticalAlign: "middle",
                        padding: "4px 48px 4px 48px",
                      }}
                    >
                      <textarea
                        ref={(el) => {
                          if (el) {
                            el.style.height = "auto";
                            el.style.height =
                              Math.min(el.scrollHeight, 100) + "px";
                          }
                        }}
                        style={{
                          width: "100%",
                          minHeight: 30,
                          maxHeight: 90, // Set max height to 100px
                          fontSize: 15,
                          fontWeight: 400,
                          color: "#222",
                          border: isEditing ? "1px solid #007bff" : "none",
                          outline: "none",
                          background: isEditing ? "#fff" : "transparent",
                          padding: isEditing ? "4px" : 0,
                          margin: 0,
                          textIndent: 0,
                          textAlign: "left",
                          boxSizing: "border-box",
                          resize: "none",
                          overflowY: "auto", // Changed from hidden to auto to show scrollbar when needed
                        }}
                        value={
                          isEditing
                            ? editingJustifications[tech.tech_radar_id] || ""
                            : justifications[tech.tech_radar_id] || ""
                        }
                        disabled={!isEditing}
                        onChange={(e) => {
                          if (isEditing) {
                            handleEditingJustificationChange(
                              tech.tech_radar_id,
                              e.target.value
                            );
                            // Auto-resize with max height limit
                            e.target.style.height = "auto";
                            e.target.style.height =
                              Math.min(e.target.scrollHeight, 90) + "px";
                          }
                        }}
                      />
                    </td>
                    <td
                      style={{
                        width: "100px",
                        textAlign: "center",
                      }}
                    >
                      {isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <SafButton
                            appearance="secondary"
                            onClick={() => handleRowSave(tech.tech_radar_id)}
                          >
                            <SafIcon
                              icon-name="check"
                              appearance="solid"
                              style={{
                                cursor: "pointer",
                                color: "#28a745",
                                fontSize: "20px",
                              }}
                            />
                          </SafButton>
                          <SafButton
                            appearance="secondary"
                            onClick={handleRowCancel}
                          >
                            <SafIcon
                              icon-name="xmark"
                              appearance="solid"
                              style={{
                                cursor: "pointer",
                                color: "#dc3545",
                                fontSize: "20px",
                              }}
                            />
                          </SafButton>
                        </div>
                      ) : (
                        <SafIcon
                          icon-name="pencil"
                          appearance="solid"
                          style={{
                            cursor: editingRowId ? "not-allowed" : "pointer",
                            color: editingRowId ? "#ccc" : "#404040",
                            fontSize: "16px",
                          }}
                          onClick={() => {
                            if (!editingRowId) {
                              handleRowEdit(tech.tech_radar_id);
                            }
                          }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </SafTable>
        </div>
      )}
    </div>
  );
};

export default JustificationComponent;
